/**
 * 舰队总览 —— **整页跑真数据**(hub 的 /fleet/summary + /alerts + /ledger/tasks + /devices)。
 *
 * 这一页原来是 mock 重灾区:舰队态势、告警区、地图缩略、进行中任务、设备健康,
 * 五块全是常量。现在前四块有真源,地图那块没有 —— 就照实说没有,不留一张假缩略图。
 *
 * 三条在这一页反复出现的规矩:
 *  · 比率带分母来历:"平均电量"后面必须跟着"按 N/M 台上报计",否则那个数就是编的;
 *  · 读失败 ≠ 没有数据:两者分开显示;
 *  · 告警能自己消失:hub 每 10 秒判定一次,条件没了自动消解,这里只显示与确认。
 */
import { useFleetSummary, useAlerts, useTasks, useTerminals } from '@/hooks/useHubData';
import { LoadError, NoDataSource } from '@/components/feature/DataSource';
import FleetStatusCard from './components/FleetStatusCard';
import AlertArea from './components/AlertArea';
import ActiveTasks from './components/ActiveTasks';
import DeviceHealth from './components/DeviceHealth';

export default function OverviewPage() {
  const { summary, loading, error, refetch } = useFleetSummary();
  const alerts = useAlerts();
  const { tasks, taskUnits, loading: tasksLoading } = useTasks();
  const { terminals, reporting, telemetryError } = useTerminals();

  return (
    <div className="p-4 lg:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-lg font-bold text-foreground-900 font-heading">舰队总览</h1>
          <p className="text-xs text-foreground-500 mt-1">
            在线状态 · 告警 · 任务进展 · 设备健康
            {summary && <span className="ml-2 text-foreground-400">数据截至 {new Date(summary.generatedAt * 1000).toLocaleTimeString('zh-CN')}</span>}
          </p>
        </div>
        <button onClick={() => { refetch(); alerts.refetch(); }}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-background-200/70 text-foreground-500 hover:bg-background-100 cursor-pointer whitespace-nowrap">
          <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`} />刷新
        </button>
      </div>

      {error && <div className="mb-4"><LoadError error={error} onRetry={refetch} /></div>}
      {telemetryError && (
        <div className="mb-4"><LoadError error={`读遥测失败:${telemetryError}(这不等于「设备都没上报」)`} /></div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 lg:gap-4">
        <div className="lg:col-span-1"><FleetStatusCard summary={summary} loading={loading} /></div>
        <div className="lg:col-span-2"><AlertArea {...alerts} /></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 lg:gap-4 mt-3 lg:mt-4">
        <div className="lg:col-span-2"><ActiveTasks tasks={tasks} units={taskUnits} loading={tasksLoading} /></div>
        <div className="lg:col-span-1"><DeviceHealth terminals={terminals} reporting={reporting} /></div>
      </div>

      <div className="mt-3 lg:mt-4 bg-background-50 border border-background-200/70 rounded-xl">
        <NoDataSource
          title="地图缩略暂无数据源"
          reason="设备位姿在自己的 map 帧里(不是经纬度),要叠到地图上得先有场地锚点标定;室内地图(PCD/栅格)也还没有上传通道。没有锚点就不画 —— 画一个猜的位置比空着更糟。"
          phase="P5 · 地图聚合上传 + 场地锚点"
        />
      </div>
    </div>
  );
}
